import { A } from "@solidjs/router";
import { Show } from "solid-js";
import { useI18n } from "@/api/i18n";
import { Button } from "@/components/ui/button";
import { limitLabel } from "../utils/check-limit";
import type { EligibilityVerdict } from "../utils/eligibility";

type Props = {
  verdict: EligibilityVerdict;
};

const BILLING_SETTINGS = "/profile/company/settings/billing";
const UPGRADE = "/profile/company/upgrade";
const ATS = "/profile/company/ats";

/**
 * Blocking upgrade interstitial shown by `EligibilityGuard` at a guarded entry.
 * Reason-aware (D5): degraded billing routes to fix-payment first, an exhausted
 * quota routes to upgrade / manage offers. Layout-agnostic centered card so it
 * works both inside a company content container and as a full-screen wrapper.
 */
export function PaywallInterstitial(props: Props) {
  const { t } = useI18n();
  const isQuota = () => props.verdict.reason === "quota_exhausted";

  return (
    <div class="w-full flex justify-center px-4 py-12">
      <div class="w-full max-w-lg rounded-2xl border bg-white p-8 flex flex-col items-center text-center gap-5 shadow-sm">
        <div class="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center">
          <i
            class={`ph-fill ${isQuota() ? "ph-rocket-launch" : "ph-warning-circle"} text-primary text-3xl`}
          />
        </div>

        <div class="flex flex-col gap-2">
          <h2 class="text-xl font-semibold text-gray-900">
            <Show
              when={isQuota()}
              fallback={t(
                "billing.eligibility_guard.block.billing_degraded.headline",
              )}
            >
              {t("billing.eligibility_guard.block.quota_exhausted.headline")}
            </Show>
          </h2>
          <p class="text-sm text-gray-600 max-w-md">
            <Show
              when={isQuota()}
              fallback={t(
                "billing.eligibility_guard.block.billing_degraded.body",
              )}
            >
              {t("billing.eligibility_guard.block.quota_exhausted.body", {
                used: String(props.verdict.used ?? 0),
                limit: String(props.verdict.limit ?? 0),
                limit_label: limitLabel(props.verdict.limitKey ?? ""),
              })}
            </Show>
          </p>
        </div>

        <div class="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          <Show
            when={isQuota()}
            fallback={
              <>
                <Button as={A} href={BILLING_SETTINGS}>
                  {t(
                    "billing.eligibility_guard.block.billing_degraded.primary_cta",
                  )}
                </Button>
                <Button variant="outline" as={A} href={UPGRADE}>
                  {t(
                    "billing.eligibility_guard.block.billing_degraded.secondary_cta",
                  )}
                </Button>
              </>
            }
          >
            <Button as={A} href={UPGRADE}>
              {t("billing.eligibility_guard.block.quota_exhausted.primary_cta")}
            </Button>
            <Button variant="outline" as={A} href={ATS}>
              {t(
                "billing.eligibility_guard.block.quota_exhausted.secondary_cta",
              )}
            </Button>
          </Show>
        </div>
      </div>
    </div>
  );
}
